import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Check, ShieldAlert } from "lucide-react";

export default function CredentialsModal({ open, onClose, credentials }) {
  const [copied, setCopied] = useState(null);

  if (!credentials) return null;

  const copyValue = (label, value) => {
    navigator.clipboard.writeText(value);
    setCopied(label);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(null), 1500);
  };

  const copyAll = () => {
    const text =
      `Login URL: ${window.location.origin}/login
` +
      (credentials.employee_code ? `Employee Code: ${credentials.employee_code}
` : "") +
      `Email: ${credentials.email}
` +
      `Temporary Password: ${credentials.password}

` +
      `You will be asked to change this password on first login.`;
    navigator.clipboard.writeText(text);
    toast.success("All details copied");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="credentials-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-[#F97316]" />
            Save these credentials
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900">
            <strong>This password will NOT be shown again.</strong> Copy it and share securely with the user. They will be required to change it on first login.
          </div>

          {credentials.employee_code && (
            <Row label="Employee Code" value={credentials.employee_code} onCopy={() => copyValue("Employee Code", credentials.employee_code)} copied={copied === "Employee Code"} mono />
          )}
          <Row label="Email" value={credentials.email} onCopy={() => copyValue("Email", credentials.email)} copied={copied === "Email"} />
          <Row label="Temporary Password" value={credentials.password} onCopy={() => copyValue("Password", credentials.password)} copied={copied === "Password"} mono highlight />

          {credentials.email_requested && (
            <div className="p-3 bg-zinc-100 border border-zinc-200 rounded-sm text-xs text-zinc-600">
              📧 Email delivery requested but not configured. Configure Resend/SendGrid in a future iteration to enable automatic email.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={copyAll} data-testid="copy-all-creds" className="rounded-sm">
            <Copy className="h-4 w-4 mr-2" /> Copy all
          </Button>
          <Button onClick={onClose} data-testid="close-creds-modal" className="btn-pressable rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
            I've saved it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, onCopy, copied, mono, highlight }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">{label}</div>
      <div className={`flex items-center gap-2 px-3 py-2.5 border rounded-sm ${highlight ? "border-[#F97316] bg-orange-50" : "border-zinc-200 bg-zinc-50"}`}>
        <span className={`flex-1 text-sm break-all ${mono ? "font-mono" : ""}`}>{value}</span>
        <button
          onClick={onCopy}
          className="text-zinc-500 hover:text-zinc-900 p-1"
          data-testid={`copy-${label.toLowerCase().replace(/ /g, "-")}`}
        >
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
